import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HiArrowUpRight } from "react-icons/hi2";
import api from "../../lib/api";
import StackIcon, { ICON_KEYS } from "../../components/StackIcon";
import ImageUploader from "./ImageUploader";

export function StackList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/stack/admin");
      setItems(data.items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.label}"?`)) return;
    await api.delete(`/api/stack/admin/${s.id}`);
    load();
  };

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">Studio</span>
          <h1 style={{ marginTop: 8 }}>Tech stack (About)</h1>
        </div>
        <button type="button" className="btn" onClick={() => nav("/admin/stack/new")}>
          New item <span className="arrow"><HiArrowUpRight /></span>
        </button>
      </div>

      {loading && <p className="text-muted">Loading…</p>}
      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      {!loading && !error && (
        <div className="admin-card" style={{ padding: 0 }}>
          {items.length === 0 ? (
            <div style={{ padding: 36, textAlign: "center", color: "var(--muted)" }}>No items.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Label</th>
                  <th>Kind</th>
                  <th>Icon key</th>
                  <th>Order</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontSize: 28 }}>
                      <StackIcon iconKey={s.iconKey} iconUrl={s.iconUrl} label={s.label} />
                    </td>
                    <td><Link to={`/admin/stack/${s.id}`}>{s.label}</Link></td>
                    <td style={{ fontSize: 13, color: "var(--muted)" }}>{s.kind}</td>
                    <td style={{ fontSize: 13, color: "var(--muted)" }}>{s.iconKey || "—"}</td>
                    <td style={{ color: "var(--muted)", fontSize: 13 }}>{s.order ?? 0}</td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 8 }}>
                        <Link className="chip" to={`/admin/stack/${s.id}`}>Edit</Link>
                        <button type="button" className="chip" style={{ cursor: "pointer", color: "var(--danger)", borderColor: "rgba(255,107,107,0.3)" }} onClick={() => remove(s)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

const EMPTY = { label: "", iconKey: "", iconUrl: "", kind: "tool", order: 0, slug: "" };
const KINDS = ["language", "framework", "platform", "database", "tool", "cloud", "other"];

export function StackEditor() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const [item, setItem] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isNew) return;
    let live = true;
    api.get(`/api/stack/admin/${id}`)
      .then(({ data }) => { if (live) setItem({ ...EMPTY, ...data.item }); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, isNew]);

  const set = (k, v) => setItem((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!item.label) return setError("Label required.");
    setBusy(true); setError(null);
    try {
      if (isNew) {
        const { data } = await api.post("/api/stack/admin", item);
        nav(`/admin/stack/${data.item.id}`, { replace: true });
      } else {
        const { data } = await api.patch(`/api/stack/admin/${id}`, item);
        setItem({ ...EMPTY, ...data.item });
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div>
      <div className="admin-head">
        <div>
          <span className="eyebrow">{isNew ? "New" : "Edit"} stack item</span>
          <h1 style={{ marginTop: 8 }}>{item.label || "Untitled"}</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <Link className="btn btn--ghost" to="/admin/stack">Back</Link>
          <button type="button" className="btn" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <p style={{ color: "var(--danger)" }}>{error}</p>}

      <div className="admin-card" style={{ maxWidth: 720 }}>
        <div className="row">
          <div className="field">
            <label>Label</label>
            <input className="input" value={item.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Shopify" />
          </div>
          <div className="field">
            <label>Kind</label>
            <select className="select" value={item.kind} onChange={(e) => set("kind", e.target.value)}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Icon key (built-in)</label>
          <input
            className="input"
            list="icon-keys"
            value={item.iconKey}
            onChange={(e) => set("iconKey", e.target.value.toLowerCase().trim())}
            placeholder="e.g. shopify, react, nodedotjs"
          />
          <datalist id="icon-keys">
            {ICON_KEYS.map((k) => <option key={k} value={k} />)}
          </datalist>
          <small style={{ color: "var(--muted)", fontSize: 12, marginTop: 6 }}>
            Pick one of the supported keys (autocomplete). Leave empty to use a custom icon below.
          </small>
        </div>

        <ImageUploader
          label="Custom icon (optional, overrides icon key)"
          accept="image/svg+xml,image/png,image/webp"
          value={item.iconUrl}
          onChange={(u) => set("iconUrl", u)}
        />

        <div className="row">
          <div className="field">
            <label>Order (lower = first)</label>
            <input className="input" type="number" value={item.order} onChange={(e) => set("order", e.target.value)} />
          </div>
          <div className="field">
            <label>Slug</label>
            <input className="input" value={item.slug || ""} placeholder="auto-from-label" onChange={(e) => set("slug", e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 16, padding: 16, background: "var(--bg)", borderRadius: 12, display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--muted)" }}>Preview</span>
          <span style={{ fontSize: 40, color: "var(--accent)" }}>
            <StackIcon iconKey={item.iconKey} iconUrl={item.iconUrl} label={item.label} />
          </span>
          <span style={{ fontFamily: "var(--font-display)" }}>{item.label || "—"}</span>
        </div>
      </div>
    </div>
  );
}
